import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { OfferingIcon } from 'partner360/components/offeringWithIcon'
import { ITabStripItem, TabStrip } from 'partner360/components/tabStrip'
import { Tag } from 'partner360/components/tag'
import { TrainingCentre } from 'partner360/components/trainingCentre/TrainingCentre'
import { MANAGER_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/managerCockpitTourTargets'
import { PROGRAM_TYPE_TAB_ICON_SIZES } from 'partner360/features/financialCockpit/configs/programTypeTabIconSizes'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from 'partner360/features/financialCockpit/configs/translations'

import { getManagerOfferingsInfo, getOfferingTitleKey } from '../../lib'
import {
  selectCockpitCategoriesByOffering,
  selectCockpitCategory,
  selectCockpitCompletenessData,
  selectCockpitOffering,
  selectVisibleCockpitOfferingsCatalog,
  setCockpitCategory,
  setCockpitOffering,
} from '../../model'
import { isCockpitPlanFinished, useManagerCockpitPlanningStatuses } from '../../model/hooks'
import type { ICockpitPlanningStatus } from '../../model/hooks'
import styles from '../cockpitProgramTypes/CockpitProgramTypes.module.scss'
import { PlanningStatusLine } from '../cockpitScopePanel/components/cockpitPlanningStatus'
import { CategoryDropdown } from '../components/categoryDropdown'

interface IManagerCockpitProgramTypesProps {
  /** Inside `ManagerCockpitScopePanel` the strip has no card of its own and no Training Centre
   *  button: the tray carries Help & tools, and the category is a chip beside the row. */
  embedded?: boolean
}

export const ManagerCockpitProgramTypes = ({
  embedded = false,
}: IManagerCockpitProgramTypesProps) => {
  const dispatch = useAppDispatch()

  const offeringsCatalog = useSelector(selectVisibleCockpitOfferingsCatalog)
  const completenessData = useSelector(selectCockpitCompletenessData)
  const selectedOffering = useSelector(selectCockpitOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const categoriesByOffering = useSelector(selectCockpitCategoriesByOffering)
  const planningStatuses = useManagerCockpitPlanningStatuses()

  const offeringCounts = useMemo(
    () => getManagerOfferingsInfo(offeringsCatalog, completenessData),
    [offeringsCatalog, completenessData]
  )

  const showOverall = offeringCounts.length !== 1

  const handleSelect = useCallback(
    (programType: ProgramTypes) => {
      dispatch(setCockpitOffering(programType))
    },
    [dispatch]
  )

  /* Only the embedded strip previews the status, in the tab's tooltip. */
  const getStatusTooltip = useCallback(
    (status?: ICockpitPlanningStatus) =>
      embedded && status
        ? () => <PlanningStatusLine status={status} layout="column" inverted />
        : undefined,
    [embedded]
  )

  useEffect(() => {
    if (!offeringCounts.length) {
      return
    }

    const isValidSelection =
      (showOverall && selectedOffering === ProgramTypes.Overall) ||
      offeringCounts.some(({ offering }) => offering === selectedOffering)

    if (!isValidSelection) {
      dispatch(setCockpitOffering(showOverall ? ProgramTypes.Overall : offeringCounts[0].offering))
    }
  }, [dispatch, offeringCounts, selectedOffering, showOverall])

  useEffect(() => {
    if (
      selectedCategory &&
      !categoriesByOffering[selectedOffering]?.some(({ category }) => category === selectedCategory)
    ) {
      dispatch(setCockpitCategory(null))
    }
  }, [categoriesByOffering, dispatch, selectedCategory, selectedOffering])

  const items = useMemo<ITabStripItem<ProgramTypes>[]>(
    () => [
      ...(showOverall
        ? [
            {
              key: ProgramTypes.Overall,
              icon: <i className={cn(styles.overallIcon, 'kbase kbase-buffer')} />,
              title: FINANCIAL_COCKPIT_TRANSLATION_KEYS.programTypes.titles.overall,
              tone: planningStatuses.overall.tone,
              count: planningStatuses.overall.attentionCount,
              unfinished: !isCockpitPlanFinished(planningStatuses.overall),
              tooltip: getStatusTooltip(planningStatuses.overall),
            },
          ]
        : []),
      ...offeringCounts.map(({ offering }) => {
        const isSelected = offering === selectedOffering
        const isCategorySelected = isSelected && Boolean(selectedCategory)
        const categories = categoriesByOffering[offering] ?? []
        /* The tab carries its icon and title only; the counts behind the fold head What needs
           attention, and are previewed in the tab's tooltip. */
        const status = planningStatuses.byOffering.get(offering)

        return {
          key: offering,
          icon: <OfferingIcon offering={offering} sizes={PROGRAM_TYPE_TAB_ICON_SIZES} />,
          title: getOfferingTitleKey(offering),
          secondaryContent:
            isCategorySelected && !embedded ? (
              <Tag variant="info" translation={selectedCategory} />
            ) : undefined,
          tone: status?.tone,
          count: status?.attentionCount,
          unfinished: status ? !isCockpitPlanFinished(status) : undefined,
          tooltip: getStatusTooltip(status),
          adornment:
            !embedded && isSelected && categories.length > 0 ? (
              <CategoryDropdown
                categories={categories}
                selectedCategory={selectedCategory}
                onChange={(category) => dispatch(setCockpitCategory(category))}
              />
            ) : undefined,
        }
      }),
    ],
    [
      categoriesByOffering,
      dispatch,
      embedded,
      getStatusTooltip,
      offeringCounts,
      planningStatuses,
      selectedCategory,
      selectedOffering,
      showOverall,
    ]
  )

  return (
    <div
      className={cn(styles.root, { [styles.embedded]: embedded })}
      data-tour-id={TOUR_TARGETS.programmeTabs}
    >
      <TabStrip
        items={items}
        selectedKey={selectedOffering}
        onSelect={handleSelect}
        appearance={embedded ? 'segmented' : 'underline'}
      />

      {!embedded && <TrainingCentre />}
    </div>
  )
}
